// Leitor do MARCADOR do "Anuncie seu imóvel com IA" (CONTRATO.md §2).
//
// O HTML das "Páginas do Site" entra no site por dangerouslySetInnerHTML, que NÃO executa
// <script>. O builder (imobtotal-web) grava então um bloco morto:
//
//   <div class="itp-anuncie-ia" data-itp-block="anuncie-ia" data-anuncie="{&quot;v&quot;:1,…}">
//     <div class="itp-anuncie-fallback">…texto com o contato da imobiliária…</div>
//   </div>
//
// e o site troca esse bloco INTEIRO pelo componente React. Quando qualquer coisa aqui falha
// (sem marcador, JSON quebrado, versão desconhecida, token vazio = tenant fora do portão)
// devolvemos `null` e a página segue renderizando o HTML como sempre — o visitante vê o
// texto de fallback do próprio marcador.
//
// Espelho de `lib/avm/placeholder.ts` / `lib/tc/placeholder.ts`, de propósito SEM importar deles:
// os leitores são independentes e a compilação isolada do teste
// (scripts/test-anuncie-placeholder.mjs) não precisa de nada além deste arquivo.
//
// ⚠️ Arquivo PURO (sem React, sem DOM, sem imports de runtime): roda no servidor (site-props da
// home), no navegador e no teste `node scripts/test-anuncie-placeholder.mjs`.

import type { AnuncieConfig, AnuncieModalidade, AnuncieSplit, AnuncieTheme } from './types';

/** Defaults do contrato §2 — tudo fora de `v`/`codsite`/`token` é opcional no JSON. */
export const ANUNCIE_THEME_PADRAO: AnuncieTheme = {
    primary: '#0b3d5c',
    secondary: '#c9a24b',
    bg: '#ffffff',
    text: '#1f2937',
    surface: '#f8fafc',
    muted: '#6b7280',
};

/** Textos NOSSOS e neutros (nada da referência externa): valem quando o builder não mandar. */
export const ANUNCIE_TEXTOS_PADRAO = {
    titulo: 'Anuncie seu imóvel',
    subtitulo:
        'Conte com as suas palavras como é o imóvel e mande algumas fotos. A nossa equipe confere e a IA monta o anúncio para você aprovar.',
    textoApoio: 'Sem formulário comprido: escreva como você contaria para alguém. Nada vai ao ar sem a sua aprovação.',
    ctaEnviar: 'Enviar meu imóvel',
    mensagemSucesso:
        'Recebemos o seu imóvel! A nossa equipe vai conferir as informações e falar com você pelo WhatsApp em breve.',
    consentText:
        'Autorizo o contato e o tratamento dos meus dados para avaliar e anunciar o imóvel, conforme a Política de Privacidade.',
    leadOrigin: 'Anuncie seu imóvel',
};

/** Teto do contrato §0: até 5 fotos por solicitação. */
export const ANUNCIE_MAX_FOTOS = 5;

const MODALIDADES: AnuncieModalidade[] = ['venda', 'aluguel'];

/**
 * `<div … class="… itp-anuncie-ia …" …>` — a ordem dos atributos do builder pode mudar. A classe
 * tem de aparecer INTEIRA (cercada por espaço/aspas): `itp-anuncie-ia-x` não conta.
 */
const RE_ABERTURA = /<div\b[^>]*\bclass\s*=\s*"(?:[^"]*\s)?itp-anuncie-ia(?:\s[^"]*)?"[^>]*>/i;
const RE_DATA_ANUNCIE = /\bdata-anuncie\s*=\s*"([^"]*)"/i;
/** varredura de <div …> e </div> a partir do fim da tag de abertura. */
const RE_TAG_DIV = /<div\b[^>]*>|<\/div\s*>/gi;

/**
 * Desfaz o escape de ATRIBUTO HTML que o builder aplica no JSON.
 * `&amp;` vem por ÚLTIMO de propósito: senão `&amp;quot;` (um `&quot;` literal no texto)
 * viraria uma aspa de verdade e quebraria o JSON.
 */
export function desescaparAtributoHtml(valor: string): string {
    return String(valor)
        .replace(/&quot;/g, '"')
        .replace(/&#0*34;/g, '"')
        .replace(/&#0*39;/g, "'")
        .replace(/&apos;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');
}

/** texto do builder (aparado e com teto) ou o padrão quando vier vazio/inválido. */
function texto(valor: unknown, padrao: string, maximo: number): string {
    return typeof valor === 'string' && valor.trim() ? valor.trim().slice(0, maximo) : padrao;
}

function booleano(valor: unknown, padrao: boolean): boolean {
    if (typeof valor === 'boolean') return valor;
    if (valor === 'true' || valor === 1 || valor === '1') return true;
    if (valor === 'false' || valor === 0 || valor === '0') return false;
    return padrao;
}

/**
 * Cor só entra no `style` do wrapper (custom properties) se for reconhecível.
 * Não é XSS (o React usa setProperty), mas lixo no CSS quebraria o visual em silêncio.
 */
function cor(valor: unknown, padrao: string): string {
    const v = typeof valor === 'string' ? valor.trim() : '';
    if (!v || v.length > 32) return padrao;
    if (/^#[0-9a-f]{3,8}$/i.test(v)) return v;
    if (/^rgba?\(\s*[\d.\s,%/]+\)$/i.test(v)) return v;
    if (/^hsla?\(\s*[\d.\s,%/deg]+\)$/i.test(v)) return v;
    if (/^[a-z]{3,20}$/i.test(v)) return v; // nomes CSS (white, black, …)
    return padrao;
}

function inteiroOuNulo(valor: unknown): number | null {
    const n = typeof valor === 'number' ? valor : parseInt(String(valor ?? ''), 10);
    return Number.isFinite(n) && Number.isInteger(n) ? n : null;
}

/** `["venda","aluguel"]` saneado: só os 2 valores do contrato, sem repetição; vazio → os dois. */
function modalidades(valor: unknown): AnuncieModalidade[] {
    if (!Array.isArray(valor)) return MODALIDADES.slice();
    const saida = MODALIDADES.filter((m) => valor.some((v) => typeof v === 'string' && v.trim().toLowerCase() === m));
    return saida.length ? saida : MODALIDADES.slice();
}

/** Aplica os defaults do contrato sobre o JSON cru. Devolve `null` se o mínimo não vier. */
export function normalizarAnuncieConfig(cru: unknown): AnuncieConfig | null {
    if (!cru || typeof cru !== 'object' || Array.isArray(cru)) return null;
    const o = cru as Record<string, unknown>;

    if (Number(o.v) !== 1) return null;

    const codsite = inteiroOuNulo(o.codsite);
    if (!codsite || codsite <= 0) return null;

    // token vazio = tenant fora do portão do Anuncie com IA (o builder serializa `token: ""`).
    // Nesse caso o site mostra o FALLBACK do marcador, não o formulário — HTML igual ao de hoje.
    if (typeof o.token !== 'string' || !o.token.trim()) return null;

    const temaCru = (o.theme && typeof o.theme === 'object' ? o.theme : {}) as Record<string, unknown>;
    const fotosCru = (o.fotos && typeof o.fotos === 'object' ? o.fotos : {}) as Record<string, unknown>;
    const fotosAtivas = booleano(fotosCru.ativas, true);
    const maxCru = inteiroOuNulo(fotosCru.max);
    const maxFotos = maxCru === null ? ANUNCIE_MAX_FOTOS : Math.max(1, Math.min(ANUNCIE_MAX_FOTOS, maxCru));

    return {
        v: 1,
        codsite,
        token: o.token.trim(),
        pageId: inteiroOuNulo(o.pageId),
        blockId: typeof o.blockId === 'string' && o.blockId.trim() ? o.blockId.trim().slice(0, 64) : null,
        titulo: texto(o.titulo, ANUNCIE_TEXTOS_PADRAO.titulo, 200),
        subtitulo: texto(o.subtitulo, ANUNCIE_TEXTOS_PADRAO.subtitulo, 600),
        textoApoio: texto(o.textoApoio, ANUNCIE_TEXTOS_PADRAO.textoApoio, 1000),
        ctaEnviar: texto(o.ctaEnviar, ANUNCIE_TEXTOS_PADRAO.ctaEnviar, 60),
        mensagemSucesso: texto(o.mensagemSucesso, ANUNCIE_TEXTOS_PADRAO.mensagemSucesso, 600),
        consentText: texto(o.consentText, ANUNCIE_TEXTOS_PADRAO.consentText, 1000),
        leadOrigin: texto(o.leadOrigin, ANUNCIE_TEXTOS_PADRAO.leadOrigin, 120),
        // fotos desligadas = max 0: o componente nem desenha o campo
        fotos: { ativas: fotosAtivas, max: fotosAtivas ? maxFotos : 0 },
        modalidades: modalidades(o.modalidades),
        mostrarComoFunciona: booleano(o.mostrarComoFunciona, true),
        mostrarOQueEntende: booleano(o.mostrarOQueEntende, true),
        theme: {
            primary: cor(temaCru.primary, ANUNCIE_THEME_PADRAO.primary),
            secondary: cor(temaCru.secondary, ANUNCIE_THEME_PADRAO.secondary),
            bg: cor(temaCru.bg, ANUNCIE_THEME_PADRAO.bg),
            text: cor(temaCru.text, ANUNCIE_THEME_PADRAO.text),
            surface: cor(temaCru.surface, ANUNCIE_THEME_PADRAO.surface),
            muted: cor(temaCru.muted, ANUNCIE_THEME_PADRAO.muted),
        },
    };
}

/**
 * Acha o PRIMEIRO marcador e devolve o HTML de antes, a config, o fallback interno e o HTML
 * de depois. Nada encontrado / inválido → `null` (a página cai no comportamento de hoje).
 */
export function extrairAnunciePlaceholder(html: unknown): AnuncieSplit | null {
    // atalho barato: roda para CADA item de menu na home (site-props) — sem o nome, nem regex.
    if (typeof html !== 'string' || html.indexOf('itp-anuncie-ia') === -1) return null;

    const abertura = RE_ABERTURA.exec(html);
    if (!abertura || abertura.index == null) return null;

    const tagAbertura = abertura[0];
    const dataAnuncie = RE_DATA_ANUNCIE.exec(tagAbertura);
    if (!dataAnuncie) return null;

    let cru: unknown;
    try {
        cru = JSON.parse(desescaparAtributoHtml(dataAnuncie[1]));
    } catch {
        return null;
    }

    const config = normalizarAnuncieConfig(cru);
    if (!config) return null;

    // Fim do bloco: conta <div>/</div> a partir do fim da tag de abertura. O contrato garante
    // 1 div interno (o fallback), mas contar também aguenta 0 ou espaços entre os fechamentos.
    const inicioConteudo = abertura.index + tagAbertura.length;
    RE_TAG_DIV.lastIndex = inicioConteudo;
    let profundidade = 1;
    let fim = -1;
    let fimConteudo = -1;
    let tag: RegExpExecArray | null;
    while ((tag = RE_TAG_DIV.exec(html)) !== null) {
        if (tag[0].charAt(1) === '/') {
            profundidade -= 1;
            if (profundidade === 0) {
                fimConteudo = tag.index;
                fim = tag.index + tag[0].length;
                break;
            }
        } else {
            profundidade += 1;
        }
    }
    RE_TAG_DIV.lastIndex = 0;
    if (fim === -1) return null; // marcador não fechado: não mexe no HTML

    return {
        antes: html.slice(0, abertura.index),
        config,
        fallback: html.slice(inicioConteudo, fimConteudo),
        depois: html.slice(fim),
    };
}

/**
 * Item de menu "leve" que a HOME manda ao cliente (`optimizeHomePayloadForClient` em
 * lib/server/site-props.ts): `Conteudo` zerado como sempre e `anuncieIa: true` SÓ no item cujo
 * Conteudo tem o marcador VÁLIDO (com token). Nos demais a chave NÃO existe — o JSON do
 * `__NEXT_DATA__` sai byte a byte igual ao de antes (mesmas chaves, mesma ordem).
 */
export function menuLeveDaHome(menu: any): any {
    const leve = { ...menu, Conteudo: '' };
    if (extrairAnunciePlaceholder(menu?.Conteudo)) leve.anuncieIa = true;
    return leve;
}
